<template>
  <div>
    <spinner-round
      :is-loading="loading"
      v-if="loading"></spinner-round>
    <table-controls-vue
      :has-sticky="true"
      v-if="!loading && sourceItems.length">
      <table class="table is-narrow is-fullwidth is-striped-v is-first-fixed-medium is-last-fixed-medium has-actionsontd">
        <thead class="has-cells-vcentered">
          <tr class="is-size-6">
            <th
              class="is-paddingless has-text-centered"
              style="z-index:2;">
              <columns-toggles
                :entries="columnsList"
                :source="columns"
                @update="updateColumns"></columns-toggles>
            </th>
            <th>
              <sortable
                @sort="$emit('sort', 'name', $event)"
                sort-id="name"
                :sort="sourceSortBy.name"
                label="Raison sociale"></sortable>
            </th>
            <th
              v-if="showColumn('type')">
              <sortable
                @sort="$emit('sort', 'type', $event)"
                sort-id="type"
                :sort="sourceSortBy.type"
                label="Type"></sortable>
            </th>
            <th
              v-if="showColumn('address__city')">
              <sortable
                @sort="$emit('sort', 'address__city', $event)"
                sort-id="address__city"
                :sort="sourceSortBy.address__city"
                label="Ville"></sortable>
            </th>
            <th
              v-if="showColumn('email')">
              <span>Email</span>
            </th>
            <th
              v-if="showColumn('phone')"
              class="is-narrow">
              <span>Téléphone</span>
            </th>
            <th
              v-if="showColumn('interlocuteurs')"
              class="is-narrow">
              <span>Interlocuteurs</span>
            </th>
            <th
              v-if="showColumn('favorite')"
              class="is-narrow">
              <span>Favori</span>
            </th>
            <th
              v-if="showColumn('created_on')">
              <sortable
                @sort="$emit('sort', 'created_on', $event)"
                sort-id="created_on"
                :sort="sourceSortBy.created_on"
                label="Date"></sortable>
            </th>
            <th
              v-if="showColumn('last_change_date')">
              <sortable
                @sort="$emit('sort', 'last_change_date', $event)"
                sort-id="last_change_date"
                :sort="sourceSortBy.last_change_date"
                label="Dernière modification"></sortable>
            </th>
            <th class="has-text-centered"></th>
          </tr>
        </thead>
        <tbody class="has-cells-vcentered">
          <tr
            v-for="(t, i) in sourceItems"
            :key="t.pk"
            class="is-size-6"
            :id="'e' + t.pk">
            <td class="has-text-centered is-cell-hoverable">
              <avatar
                :item="t"
                :i="i"
                class="is-medium"></avatar>
            </td>
            <td class="is-cell-hoverable">
              <router-link
                :to="{name: 'TiersVue', params: {id: t.pk}}"
                class="fullLink">
                {{ t.name }}
              </router-link>
            </td>
            <td v-if="showColumn('type')">
              <span class="tag is-light is-border">{{ tiersTypeLabel(t.type) }}</span>
            </td>
            <td
              v-if="showColumn('address__city')"
              style="max-width: 150px"
              class="is-narrow is-truncate">
              <span v-if="t.address && (t.address.city || t.address.zip_code)">
                {{ t.address.city || '-' }} <span v-if="t.address.zip_code">({{ t.address.zip_code }})</span>
              </span>
              <span v-else>-</span>
            </td>
            <td v-if="showColumn('email')">
              <router-link
                v-if="t.email"
                :to="{name: 'TiersVue', params: {id: t.pk}}"
                :title="t.email">
                {{ t.email | truncate(35) }}
              </router-link>
              <span v-else>-</span>
            </td>
            <td
              v-if="showColumn('phone')"
              class="is-nowrap">
              <phone-number
                v-if="t.phone"
                :phone="t.phone"></phone-number>
              <span v-else>N.C</span>
            </td>
            <td
              v-if="showColumn('interlocuteurs')"
              class="has-text-centered">
              {{ t.interlocuteurs ? t.interlocuteurs.length : 0 }}
            </td>
            <td
              v-if="showColumn('favorite')"
              class="has-text-centered">
              <a @click="toggleFavorite(t)">
                <span class="icon">
                  <i
                    :class="t.favorite ? 'fas fa-star has-text-warning' : 'fal fa-star has-text-grey-light'"></i>
                </span>
              </a>
            </td>
            <td v-if="showColumn('created_on')">
              {{ t.created_on | date }}
            </td>
            <td v-if="showColumn('last_change_date')">
              {{ t.last_change_date | date }}
            </td>
            <td class="has-text-centered">
              <display-button-list
                :buttons="buttonList"
                :data="t">
              </display-button-list>
            </td>
          </tr>
        </tbody>
      </table>
    </table-controls-vue>
    <div v-show="!loading && !sourceItems.length">
      <div class="is-padding">
        <h2 class="title is-3 has-text-weight-bold">
          Aucun tiers disponible
        </h2>
        <a
          class="has-text-primary"
          @click="$emit('reset')"
          v-if="hasFilter"><span class="icon"><i
            class="fas fa-undo-alt"></i></span> <span>Réinitialiser les filtres</span></a>
      </div>
    </div>
  </div>
</template>

<script>
import { ColumnConf, Error } from '@/mixins'
import { TIERS_RESOURCE, tiersTypeLabel } from './tiersConstants'

export default {
  name: 'TiersList',
  mixins: [ColumnConf, Error],
  props: {
    sourceItems: {
      type: Array,
      default: () => []
    },
    sourceSortBy: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasFilter: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      columnsConfResource: 'tiers',
      buttonList: [
        {
          action: (id) => {
            this.$router.push({ name: 'TiersVue', params: { id } })
          },
          icon: 'fa-address-card',
          text: 'Fiche tiers'
        },
        {
          action: (id) => {
            this.$router.push({ name: 'TiersEdit', params: { id } })
          },
          icon: 'fa-user-edit',
          text: 'Modifier fiche'
        },
        {
          action: (id) => {
            this.deleteItem(id)
          },
          icon: 'fa-trash-alt',
          text: 'Supprimer tiers'
        }
      ]
    }
  },
  computed: {
    columnsShow () {
      return {
        type: { label: 'Type', mobile: true },
        address__city: { label: 'Ville', mobile: false },
        email: { label: 'Email', mobile: true },
        phone: { label: 'Téléphone', mobile: false },
        interlocuteurs: { label: 'Interlocuteurs', mobile: false },
        favorite: { label: 'Favori', mobile: false },
        created_on: { label: 'Date', mobile: false },
        last_change_date: { label: 'Dernière modification', mobile: false }
      }
    }
  },
  methods: {
    tiersTypeLabel,
    /**
     * @param {object} tiers
     */
    async toggleFavorite (tiers) {
      try {
        let { data } = await this.$api.patch(TIERS_RESOURCE, tiers.pk, { favorite: !tiers.favorite })
        tiers.favorite = data.favorite
      } catch (e) {
        this.$toasted.global.danger('Erreur lors de l\'enregistrement !')
      }
    },
    /**
     * @param {string} pk
     */
    async deleteItem (pk) {
      let value = await this.$swal({
        title: 'Suppression tiers',
        text: 'Êtes vous sûr de vouloir supprimer ce tiers ? Les éléments qui le référencent conserveront les informations déjà saisies.',
        confirmButtonText: 'Supprimer',
        focusConfirm: false,
        customClass: {
          confirmButton: 'is-danger'
        }
      })
      if (value.dismiss) return false
      try {
        await this.$api.delete(TIERS_RESOURCE, pk)
        this.$toasted.global.success('Tiers supprimé')
        this.$emit('reload')
      } catch (e) {
        if (e.response && JSON.stringify(e.response.data).includes('TIERS_IN_USE')) {
          this.$toasted.global.danger('Suppression impossible : ce tiers est utilisé — voir l\'onglet Historique de sa fiche')
        } else {
          this.handleError(e)
        }
      }
    }
  }
}
</script>
